import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function Contact() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan");
  const isDemo = searchParams.get("demo") === "true";
  const isEnterprise = searchParams.get("enterprise") === "true";

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    companySize: "",
    clientCount: "",
    location: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Déterminer le titre et la description selon le plan
  const getPlanInfo = () => {
    const plans = {
      standard: {
        title: "Essayer Standard gratuitement",
        subtitle: "14 jours d'essai gratuit",
        description: "Démarrez votre essai gratuit du plan Standard et découvrez comment Artkiv simplifie la gestion de votre collection d'art.",
      },
      premium: {
        title: "Découvrir Premium",
        subtitle: "Intelligence artificielle & insights",
        description: "Accédez aux analyses IA avancées et aux recommandations personnalisées pour votre collection.",
      },
      art_advisor: {
        title: "Demander une démo Art Advisor",
        subtitle: "Gestion multi-clients professionnelle",
        description: "Découvrez comment gérer jusqu'à 5 collections clients avec nos outils professionnels.",
      },
      art_advisor_premium: {
        title: "Demander une démo Art Advisor Premium",
        subtitle: "Solution complète pour professionnels",
        description: "Explorez notre plateforme avancée pour gérer un nombre illimité de clients avec toutes les fonctionnalités IA.",
      },
      family_office: {
        title: isEnterprise ? "Demander un devis sur-mesure" : "Découvrir Family Office",
        subtitle: "Solution institutionnelle",
        description: "Discutons de vos besoins spécifiques et établissons une solution adaptée à votre organisation.",
      },
    };

    return plans[planParam] || {
      title: "Contactez-nous",
      subtitle: "Nous sommes à votre écoute",
      description: "Une question ? Un besoin spécifique ? Notre équipe vous répond sous 24h.",
    };
  };

  const planInfo = getPlanInfo();

  // Déterminer quels champs afficher selon le plan
  const showBusinessFields = ["art_advisor", "art_advisor_premium", "family_office"].includes(planParam);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Pour le moment, on simule l'envoi
      // Plus tard : appeler une API qui envoie vers Slack
      console.log("📧 Envoi du formulaire:", { ...formData, plan: planParam, isDemo, isEnterprise });
      
      // Simuler un délai
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSuccess(true);
      
      // Rediriger après 3 secondes
      setTimeout(() => {
        navigate("/");
      }, 3000);
    } catch (error) {
      console.error("Erreur envoi:", error);
      alert("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl font-serif mb-4" style={{ fontFamily: "Georgia, serif" }}>
            Message envoyé !
          </h2>
          <p className="text-neutral-600 mb-6">
            Merci pour votre demande. Notre équipe vous contactera sous 24h.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 bg-black text-white rounded-full hover:bg-neutral-800 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-6 py-20">
        {/* Header */}
        <div className="text-center mb-12">
          <h1
            className="text-5xl md:text-6xl font-serif mb-4 tracking-tight"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {planInfo.title}
          </h1>
          <p className="text-xl text-neutral-500 mb-2">{planInfo.subtitle}</p>
          <p className="text-neutral-600 max-w-2xl mx-auto leading-relaxed">
            {planInfo.description}
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-neutral-50 rounded-2xl p-8 md:p-12 border border-neutral-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nom et Prénom */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors"
                  placeholder="Jean"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  required
                  value={formData.lastName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors"
                  placeholder="Dupont"
                />
              </div>
            </div>

            {/* Email et Téléphone */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Email professionnel <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors"
                  placeholder="jean.dupont@exemple.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Téléphone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
            </div>

            {/* Champs professionnels (seulement pour Art Advisor et Family Office) */}
            {showBusinessFields && (
              <>
                {/* Entreprise */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Nom de l'entreprise <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors"
                    placeholder="Cabinet d'Art Advisory"
                  />
                </div>

                {/* Taille et Nombre de clients */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Collaborateurs utilisant la plateforme <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="companySize"
                      required
                      value={formData.companySize}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors bg-white"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="1">1 personne</option>
                      <option value="2-5">2-5 personnes</option>
                      <option value="6-10">6-10 personnes</option>
                      <option value="11-20">11-20 personnes</option>
                      <option value="20+">Plus de 20</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Nombre de clients/collections gérés <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="clientCount"
                      required
                      value={formData.clientCount}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors bg-white"
                    >
                      <option value="">Sélectionner...</option>
                      <option value="1-5">1-5 clients</option>
                      <option value="6-10">6-10 clients</option>
                      <option value="11-20">11-20 clients</option>
                      <option value="21-50">21-50 clients</option>
                      <option value="50+">Plus de 50</option>
                    </select>
                  </div>
                </div>

                {/* Localisation */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Localisation de l'entreprise <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors"
                    placeholder="Paris, France"
                  />
                </div>
              </>
            )}

            {/* Message */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Message {!showBusinessFields && <span className="text-neutral-400">(optionnel)</span>}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                className="w-full px-4 py-3 rounded-lg border border-neutral-300 focus:border-black focus:outline-none transition-colors resize-none"
                placeholder={
                  showBusinessFields
                    ? "Décrivez vos besoins spécifiques, vos attentes, et toute information qui nous aiderait à préparer une démonstration personnalisée..."
                    : "Votre message..."
                }
              />
            </div>

            {/* Boutons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 min-w-[200px] py-4 bg-black text-white rounded-full font-semibold hover:bg-neutral-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Envoi en cours..." : isDemo ? "Demander une démo" : isEnterprise ? "Demander un devis" : "Envoyer"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-8 py-4 border-2 border-neutral-300 text-neutral-700 rounded-full font-semibold hover:border-black hover:text-black transition-all duration-300"
              >
                Retour
              </button>
            </div>

            {/* Note de confidentialité */}
            <p className="text-xs text-neutral-500 text-center pt-4">
              En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe. 
              Vos données sont traitées conformément à notre{" "}
              <a href="/politique-de-confidentialite" className="underline hover:text-black">
                politique de confidentialité
              </a>.
            </p>
          </form>
        </div>

        {/* Info supplémentaire pour Family Office */}
        {planParam === "family_office" && (
          <div className="mt-12 p-6 bg-neutral-100 rounded-xl">
            <h3 className="font-semibold text-lg mb-3">Ce qui se passe ensuite</h3>
            <ul className="space-y-2 text-sm text-neutral-700">
              <li className="flex items-start gap-2">
                <span className="text-black font-bold">1.</span>
                <span>Notre équipe vous contacte sous 24h pour comprendre vos besoins</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-black font-bold">2.</span>
                <span>Nous préparons une démonstration personnalisée (30-45 min)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-black font-bold">3.</span>
                <span>Nous établissons un devis détaillé adapté à votre organisation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-black font-bold">4.</span>
                <span>Onboarding personnalisé avec formation de votre équipe</span>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}